import React, { useEffect, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from "reactstrap"
import {
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaEye,
} from "react-icons/fa"
import Swal from "sweetalert2"
import { fetchData, FetchPutData } from "utils/Common_api_functions"
import moment from "moment"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const UserQuery = () => {
  const { checkAccess } = usePermissionData()

  const [queries, setQueries] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedQuery, setSelectedQuery] = useState(null)
  const [loading, setLoading] = useState(false)

  // 🔹 Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  useEffect(() => {
    getQueries()
  }, [])

  async function getQueries() {
    const APIdata = await fetchData(setLoading, "query")
    if (APIdata?.success) {
      setQueries(APIdata?.queries.reverse())
    } else {
      Swal.fire({
        title: "Error Fetching Queries",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const toggleModal = (query = null) => {
    if (query) {
      const currentQuery = queries.find(q => q.id === query.id)
      setSelectedQuery(currentQuery)
    } else {
      setSelectedQuery(null)
    }
    setModalOpen(!modalOpen)
  }

  const handleCloseQuery = async id => {
    const APIdata = await FetchPutData(setLoading, "query", id, {
      status: true,
    })
    if (APIdata?.data?.success) {
      Swal.fire({ title: "Status Updated", icon: "success" })
      getQueries()
      toggleModal()
    } else {
      Swal.fire({
        title: "Error updating query status",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const handleReopenQuery = async id => {
    const APIdata = await FetchPutData(setLoading, "query", id, {
      status: false,
    })
    if (APIdata?.data?.success) {
      Swal.fire({ title: "Status Updated", icon: "success" })
      getQueries()
      toggleModal()
    } else {
      Swal.fire({
        title: "Error updating query status",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  // 🔹 Filtered and paginated data
  const filteredQueries = queries.filter(query => {
    const term = searchTerm.toLowerCase()
    return (
      query.employee?.name?.toLowerCase().includes(term) ||
      query.employee?.email?.toLowerCase().includes(term) ||
      query.employee?.phone?.toLowerCase().includes(term) ||
      query.employee?.role?.roleName?.toLowerCase().includes(term) ||
      query.title?.toLowerCase().includes(term) ||
      query.description?.toLowerCase().includes(term)
    )
  })

  const totalPages = Math.ceil(filteredQueries.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const currentData = filteredQueries.slice(
    startIndex,
    startIndex + itemsPerPage
  )

  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage)
  }

  return (
    <div className="page-content">
      <PermittedComponent isAccess={checkAccess(7, 1) || checkAccess(7, 2)}>
        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Employee Queries"
          description="View and manage queries submitted by cleaning staff."
        />

        <Card className="shadow-lg">
          <CardBody>
            {/* 🔹 Top Controls */}
            <Row className="align-items-center mb-3">
              <Col md="4">
                <Input
                  type="text"
                  placeholder="Search by name, email, role or title..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </Col>
              <Col md="8" className="text-end">
                <div className="d-inline-flex align-items-center">
                  <Input
                    type="select"
                    value={itemsPerPage}
                    onChange={e => {
                      setItemsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                    style={{ width: "100px", display: "inline-block" }}
                  >
                    <option value={10}>Show 10</option>
                    <option value={20}>Show 20</option>
                    <option value={30}>Show 30</option>
                    <option value={40}>Show 40</option>
                    <option value={50}>Show 50</option>
                  </Input>
                </div>
              </Col>
            </Row>

            {/* 🔹 Table */}
            <Table bordered hover responsive>
              <thead className="bg-blue text-white">
                <tr>
                  <th className="text-center">#</th>
                  <th>Employee Name</th>
                  <th>Email Address</th>
                  <th>Contact Number</th>
                  <th>Query</th>
                  <th>Posted At</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No queries found.
                    </td>
                  </tr>
                ) : (
                  currentData.map((query, index) => (
                    <tr key={query.id}>
                      <td className="text-center">
                        {(currentPage - 1) * 10 + index + 1}
                      </td>
                      <td className="d-flex align-items-center">
                        <img
                          src={
                            query.employee?.profilePicture
                              ? `${process.env.REACT_APP_IMAGE_PATH}${query.employee.profilePicture}`
                              : "https://ui-avatars.com/api/?name=" +
                                encodeURIComponent(
                                  query.employee?.name || "User"
                                ) +
                                "&background=random"
                          }
                          alt={query.employee?.name}
                          className="rounded-circle me-2"
                          width="35"
                          height="35"
                          style={{ objectFit: "cover" }}
                        />
                        {query.employee?.name}
                      </td>
                      <td>{query.employee?.email}</td>
                      <td>{query.employee?.phone || "N/A"}</td>
                      <td>{query.title}</td>
                      <td>
                        {moment(query.createdAt).format("DD-MM-YYYY | hh:mm A")}
                      </td>
                      <td className="text-center">
                        <Badge
                          color={
                            query?.isReopened
                              ? "info"
                              : query.status
                              ? "success"
                              : "warning"
                          }
                          style={{ fontSize: "12px", padding: "8px 10px" }}
                        >
                          {query?.isReopened
                            ? "Re-open"
                            : query.status
                            ? "Closed"
                            : "Open"}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => toggleModal(query)}
                        >
                          <FaEye /> View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {/* 🔹 Pagination Controls */}
            <div className="d-flex justify-content-between  gap-2">
              <p className="mb-0">
                Showing {currentData.length} out of {queries.length} Queries.
              </p>
              <div>
                <Button
                  size="sm"
                  disabled={currentPage === 1}
                  className="fs-6"
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  <FaChevronCircleLeft />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    className="mx-2 fs-6"
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  size="sm"
                  className="fs-6"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  <FaChevronCircleRight />
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* 🔹 Modal */}
        {selectedQuery && (
          <Modal
            isOpen={modalOpen}
            toggle={() => toggleModal()}
            size="lg"
            centered
          >
            <ModalHeader toggle={() => toggleModal()}>
              Query Details
              <Badge
                color={selectedQuery.status ? "success" : "warning"}
                className="ms-2"
              >
                {selectedQuery.status ? "Closed" : "Open"}
              </Badge>
            </ModalHeader>
            <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
              <Table bordered className="border-secondary">
                <tbody>
                  <tr>
                    <th style={{ width: "30%" }}>Employee Name</th>
                    <td>{selectedQuery.employee?.name}</td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{selectedQuery.employee?.email}</td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td>{selectedQuery.employee?.phone || "N/A"}</td>
                  </tr>
                  <tr>
                    <th>Role</th>
                    <td>
                      <Badge color="info p-2">
                        {selectedQuery.employee?.role?.roleName || "N/A"}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Title</th>
                    <td>{selectedQuery.title}</td>
                  </tr>
                  <tr>
                    <th>Description</th>
                    <td>{selectedQuery.description}</td>
                  </tr>
                  <tr>
                    <th>Posted On</th>
                    <td>
                      {moment(selectedQuery.createdAt).format(
                        "DD-MM-YYYY | hh:mm A"
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </ModalBody>
            <ModalFooter>
              {!selectedQuery.status ? (
                <>
                  {checkAccess(7, 4) && (
                    <Button
                      color="success"
                      onClick={() => handleCloseQuery(selectedQuery.id)}
                    >
                      Close Query
                    </Button>
                  )}
                  <Button color="secondary" onClick={() => toggleModal()}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {checkAccess(7, 4) && (
                    <Button
                      color="danger"
                      onClick={() => handleReopenQuery(selectedQuery.id)}
                    >
                      Reopen Query
                    </Button>
                  )}
                  <Button color="secondary" onClick={() => toggleModal()}>
                    Cancel
                  </Button>
                </>
              )}
            </ModalFooter>
          </Modal>
        )}
      </PermittedComponent>
    </div>
  )
}

export default UserQuery
